import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {forkJoin, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {SummaryParent} from '@app/wrappers/summary-parent';
import {Divider} from 'primeng/divider';
import {News} from '@components/ui/news/news';
import {MainStatusTag} from '@components/ui/main-status-tag/main-status-tag';
import {AuthService} from '@services/auth.service';
import {OrchestrationAccessService} from '@services/orchestration-access.service';
import {ThemeModule} from '@app/themes/theme-module';
import {IMenuItem, MenuItemType} from '@models/i-menu-item';
import {MENU_ITEMS} from '@shared/config/menu';
import {environment} from '@root/src/environments/environment';

@Component({
  selector: 'app-homePage-page',
  standalone: true,
  imports: [CommonModule, TranslateModule,ThemeModule, Divider, News, MainStatusTag],
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
})
export class MainMenuComponent extends SummaryParent implements OnInit, OnDestroy {
  isDarkTheme = false;

  items: IMenuItem[] = [];

  private readonly allItems: IMenuItem[] = MENU_ITEMS;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private orchestrationAccess: OrchestrationAccessService,
  ) {
    super();
    this.isDarkTheme = document.body.classList.contains('dark-theme');
  }

  ngOnInit(): void {
    this.items = this.visibleItems(false);
    if (this.authService.isAuthenticated() && !this.authService.isAdmin()) {
      forkJoin({ orchestration: this.orchestrationAccess.hasOrchestrationAdmin(), access: this.orchestrationAccess.hasAccessManage() })
        .pipe(takeUntil(this.destroy$)).subscribe(allowed => {
        this.items = this.visibleItems(allowed.orchestration, allowed.access);
        this.cdr.detectChanges();
      });
    }

    const observer = new MutationObserver(() => {
      this.isDarkTheme = document.body.classList.contains('dark-theme');
      this.cdr.detectChanges();
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private visibleItems(orchestrationAdmin: boolean, accessManager = false): IMenuItem[] {
    if (this.authService.isAdmin()) {
      return [...this.allItems];
    }

    return this.allItems.flatMap((item) => {
      if (item.id === MenuItemType.AiModule) {
        return orchestrationAdmin ? [item] : [];
      }
      if (item.title !== 'Administration') {
        return [item];
      }
      if (!orchestrationAdmin && !accessManager) {
        return [];
      }
      return [{ ...item, url: orchestrationAdmin ? '/admin/process-management' : '/admin/process-management/access', children: undefined }];
    });
  }

  getIconPath(iconName: string): string {
    const theme = this.isDarkTheme ? 'dark' : 'light';
    return `assets/icons/main/${theme}/ico-${iconName}.svg`;
  }

  protected readonly environment = environment;
}
